import type { Product, ProductReviews, ProductReview } from '@smartphone-tycoon/shared-core';

interface ReviewTemplates {
  professional: {
    publications: Array<{
      name: string;
      title: string;
    }>;
    positiveTemplates: string[];
    neutralTemplates: string[];
    negativeTemplates: string[];
  };
  customer: {
    authors: string[];
    positiveTemplates: string[];
    neutralTemplates: string[];
    negativeTemplates: string[];
  };
}

const REVIEW_TEMPLATES: ReviewTemplates = {
  professional: {
    publications: [
      { name: "TechCrunch", title: "Senior Technology Editor" },
      { name: "The Verge", title: "Mobile Technology Reviewer" },
      { name: "Wired", title: "Hardware Specialist" },
      { name: "Engadget", title: "Consumer Electronics Editor" },
      { name: "GSMArena", title: "Mobile Device Analyst" }
    ],
    positiveTemplates: [
      "Impressive engineering and cutting-edge features make this a standout device in its category.",
      "A compelling product that successfully targets its intended market with strong appeal.",
      "Excellent build quality and innovative features set this apart from the competition.",
      "Outstanding performance and design make this a top choice in the segment.",
      "Remarkable technology integration delivers exceptional user experience."
    ],
    neutralTemplates: [
      "Solid technical implementation with some notable innovations.",
      "Shows promise in the market with good positioning and features.",
      "Competent device that covers the basics adequately.",
      "Decent offering with both strengths and areas for improvement.",
      "Well-executed product that meets market expectations."
    ],
    negativeTemplates: [
      "Faces challenges in standing out in a competitive marketplace.",
      "Limited innovation fails to justify the premium positioning.",
      "Several design compromises detract from the overall experience.",
      "Struggles to differentiate itself from existing alternatives.",
      "Disappointing execution in key areas affects overall appeal."
    ]
  },
  customer: {
    authors: [
      "TechReviewer123", "GadgetLover", "SmartShopper", "TechEnthusiast", 
      "MobileExpert", "DeviceGuru", "PhoneFanatic", "TechSavvy", 
      "DigitalNomad", "TechCritic"
    ],
    positiveTemplates: [
      "Outstanding phone! The build quality and features are exactly what I was looking for.",
      "Amazing device! Exceeded all my expectations with incredible performance.",
      "Love this phone! Great design and fantastic features for the price.",
      "Excellent choice! Fast, reliable, and packed with useful features.",
      "Perfect phone for my needs! Highly recommend to anyone looking for quality."
    ],
    neutralTemplates: [
      "Good phone overall, though there are a few areas that could be improved.",
      "Solid device that performs well for most tasks. Worth considering.",
      "Decent phone for the price, meets my basic requirements adequately.",
      "Not bad, has some nice features but nothing particularly outstanding.",
      "Fair value, does what it needs to do without being exceptional."
    ],
    negativeTemplates: [
      "Underwhelming performance. Expected more for the price point.",
      "Several issues with build quality and feature implementation.",
      "Disappointing experience. Many competitors offer better value.",
      "Not impressed. Falls short of expectations in several key areas.",
      "Overpriced for what you get. Better alternatives available."
    ]
  }
};

export class ReviewGeneratorService {
  // Internal state for tracking review history
  private static reviewHistory: Map<string, ProductReviews> = new Map();
  private static reviewCounter: number = 0;

  /**
   * Generate reviews for a product based on its metrics
   */
  public static generateReviews(product: Product): ProductReviews {
    const professional = this.generateProfessionalReviews(product);
    const customer = this.generateCustomerReviews(product);
    
    const reviews = {
      professional,
      customer
    };

    // Store in history
    this.reviewHistory.set(product.id, reviews);
    this.reviewCounter++;

    return reviews;
  }

  private static generateProfessionalReviews(product: Product): ProductReview[] {
    const reviews: ProductReview[] = [];
    const numReviews = 2; // Always generate 2 professional reviews
    
    // Shuffle publications to get variety
    const shuffledPublications = [...REVIEW_TEMPLATES.professional.publications]
      .sort(() => Math.random() - 0.5);
    
    for (let i = 0; i < numReviews; i++) {
      const publication = shuffledPublications[i];
      const rating = this.calculateProfessionalRating(product, i);
      const text = this.selectReviewText('professional', rating);
      
      reviews.push({
        rating,
        text,
        author: publication.name,
        publication: publication.name,
        reviewerTitle: publication.title
      });
    }
    
    return reviews;
  }

  private static generateCustomerReviews(product: Product): ProductReview[] {
    const reviews: ProductReview[] = [];
    const numReviews = 3; // Always generate 3 customer reviews
    
    // Shuffle authors to get variety
    const shuffledAuthors = [...REVIEW_TEMPLATES.customer.authors]
      .sort(() => Math.random() - 0.5);
    
    for (let i = 0; i < numReviews; i++) {
      const author = shuffledAuthors[i];
      const rating = this.calculateCustomerRating(product, i);
      const text = this.selectReviewText('customer', rating);
      
      reviews.push({
        rating,
        text,
        author
      });
    }
    
    return reviews;
  }

  private static calculateProfessionalRating(product: Product, index: number): number {
    let baseRating: number;
    
    if (index === 0) {
      // First review based on tech score
      baseRating = Math.min(5, Math.max(2, Math.round(product.techScore / 20)));
    } else {
      // Second review based on market appeal
      baseRating = Math.min(5, Math.max(2, Math.round(product.marketAppeal / 20)));
    }
    
    // Add some variation (±0.5)
    const variation = (Math.random() - 0.5);
    return Math.min(5, Math.max(1, Math.round(baseRating + variation)));
  }

  private static calculateCustomerRating(product: Product, index: number): number {
    let baseRating: number;
    
    switch (index) {
      case 0:
        // Overall experience based on market appeal
        baseRating = Math.min(5, Math.max(1, Math.round(product.marketAppeal / 20)));
        break;
      case 1:
        // Tech/performance based rating
        baseRating = Math.min(5, Math.max(1, Math.round(product.techScore / 20)));
        break;
      case 2: {
        // Value-based rating (price consideration)
        const valueScore = this.calculateValueScore(product);
        baseRating = Math.min(5, Math.max(1, Math.round(valueScore / 20)));
        break;
      }
      default:
        baseRating = 4;
    }
    
    // Add more variation for customer reviews (±1)
    const variation = (Math.random() - 0.5) * 2;
    return Math.min(5, Math.max(1, Math.round(baseRating + variation)));
  }

  private static calculateValueScore(product: Product): number {
    // Calculate value score based on tech score vs price
    const priceRatio = product.price > 800 ? 0.7 : product.price > 500 ? 0.85 : 1.0;
    return product.techScore * priceRatio;
  }

  private static selectReviewText(
    type: 'professional' | 'customer', 
    rating: number
  ): string {
    const templates = REVIEW_TEMPLATES[type];
    let selectedTemplates: string[];
    
    if (rating >= 4) {
      selectedTemplates = templates.positiveTemplates;
    } else if (rating >= 3) {
      selectedTemplates = templates.neutralTemplates;
    } else {
      selectedTemplates = templates.negativeTemplates;
    }
    
    // Select random template
    const randomIndex = Math.floor(Math.random() * selectedTemplates.length);
    return selectedTemplates[randomIndex];
  }

  /**
   * Calculate average rating from reviews
   */
  public static calculateAverageRating(reviews: ProductReviews): number {
    const allRatings = [
      ...reviews.professional.map(r => r.rating),
      ...reviews.customer.map(r => r.rating)
    ];
    
    if (allRatings.length === 0) return 0;
    
    const sum = allRatings.reduce((acc, rating) => acc + rating, 0);
    return Number((sum / allRatings.length).toFixed(1));
  }

  /**
   * Reset the service to initial state
   */
  public static reset(): void {
    // console.log('ReviewGeneratorService: Resetting to initial state');
    
    // Clear review history
    this.reviewHistory.clear();
    
    // Reset counter
    this.reviewCounter = 0;
    
    // console.log('ReviewGeneratorService: Reset complete');
  }
}

export const reviewGenerator = ReviewGeneratorService;